import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { getAdvisoryConfig, updateAdvisoryConfig, assignStaffToAdvisory, assignStudentToAdvisory, addAdvisoryGroup, removeAdvisoryGroup, getStudents, getStaff, uploadAdvisoryImage, deleteAdvisoryImage } from "../../lib/api";

export function AdvisoryGroupsTab() {
  const [advisoryGroups, setAdvisoryGroups] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");
  const imageInputRefs = useRef({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const [advisoryRes, studentsRes, staffRes] = await Promise.all([
        getAdvisoryConfig(), getStudents("current", true), getStaff({ assignable: true }),
      ]);
      setAdvisoryGroups([...(advisoryRes.data.groups || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setAllStudents(studentsRes.data);
      setAllStaff([...(staffRes.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleImageUpload = async (groupNum, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2MB"); e.target.value = ""; return; }
    try {
      await uploadAdvisoryImage(groupNum, file);
      toast.success("Advisory image uploaded");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload image");
    }
    e.target.value = "";
  };

  const handleImageDelete = async (groupNum) => {
    try {
      await deleteAdvisoryImage(groupNum);
      toast.success("Advisory image removed");
      fetchData();
    } catch {
      toast.error("Failed to remove image");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Advisory Group Management</CardTitle>
              <CardDescription>Configure advisory group names and images, assign students and staff advisors</CardDescription>
            </div>
            <Button onClick={async () => {
              try { await addAdvisoryGroup(); toast.success("Advisory group added"); fetchData(); } catch { toast.error("Failed to add group"); }
            }} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-advisory-group-btn">Add Advisory Group</Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
          ) : (
            <div className="space-y-4">
              {advisoryGroups.map((group) => (
                <div key={group.group} className="border rounded-lg p-4" style={{ borderLeftWidth: '4px', borderLeftColor: group.color }}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {/* Advisory Image */}
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        ref={el => imageInputRefs.current[group.group] = el}
                        onChange={(e) => handleImageUpload(group.group, e)}
                        data-testid={`advisory-image-input-${group.group}`}
                      />
                      <div className="relative group/img shrink-0">
                        {group.image ? (
                          <div className="relative">
                            <img src={group.image} alt={group.name} className="w-12 h-12 rounded-lg object-cover border" data-testid={`advisory-image-${group.group}`} />
                            <div className="absolute inset-0 rounded-lg bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-1">
                              <button onClick={() => imageInputRefs.current[group.group]?.click()} className="p-0.5 text-white hover:text-blue-200" title="Change">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                              </button>
                              <button onClick={() => handleImageDelete(group.group)} className="p-0.5 text-white hover:text-red-300" title="Remove" data-testid={`advisory-image-remove-${group.group}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => imageInputRefs.current[group.group]?.click()} className="w-12 h-12 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-gray-400 hover:text-gray-500 transition-colors" title="Upload advisory image" data-testid={`advisory-upload-image-${group.group}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                          </button>
                        )}
                      </div>
                      {editingGroup === group.group ? (
                        <div className="flex items-center gap-2">
                          <Input value={editGroupName} onChange={(e) => setEditGroupName(e.target.value)} className="w-48" data-testid={`advisory-name-input-${group.group}`} />
                          <Button size="sm" onClick={async () => {
                            const currentConfig = advisoryGroups.reduce((acc, g) => { acc[g.group] = { name: g.name, color: g.color, image: g.image || "" }; return acc; }, {});
                            currentConfig[group.group] = { ...currentConfig[group.group], name: editGroupName };
                            await updateAdvisoryConfig({ groups: currentConfig });
                            toast.success("Advisory name updated"); setEditingGroup(null); fetchData();
                          }} className="bg-[#00305E]">Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingGroup(null)}>Cancel</Button>
                        </div>
                      ) : (
                        <>
                          <h3 className="text-lg font-semibold">{group.name}</h3>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingGroup(group.group); setEditGroupName(group.name); }} data-testid={`edit-advisory-name-${group.group}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                          </Button>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{group.students?.length || 0} students</span>
                      {advisoryGroups.length > 1 && (
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={async () => { if (window.confirm(`Remove ${group.name}?`)) { try { await removeAdvisoryGroup(group.group); toast.success("Advisory group removed"); fetchData(); } catch { toast.error("Failed"); } } }}
                          data-testid={`remove-advisory-${group.group}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Staff Advisors */}
                  <div className="mb-4">
                    <Label className="text-sm font-medium mb-2 block">Staff Advisors</Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {group.advisors?.map((advisor) => (
                        <span key={advisor.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          {advisor.name}
                          <button onClick={async () => { await assignStaffToAdvisory("none", advisor.id); toast.success("Advisor removed"); fetchData(); }} className="hover:text-red-600">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                          </button>
                        </span>
                      ))}
                      {(!group.advisors || group.advisors.length === 0) && <span className="text-sm text-muted-foreground italic">No advisors assigned</span>}
                    </div>
                    <Select value="" onValueChange={async (staffId) => { if (staffId) { await assignStaffToAdvisory(group.group, staffId); toast.success("Advisor added"); fetchData(); } }}>
                      <SelectTrigger className="w-64" data-testid={`advisory-staff-select-${group.group}`}><SelectValue placeholder="Add staff advisor..." /></SelectTrigger>
                      <SelectContent>
                        {allStaff.filter(s => !group.advisors?.some(a => a.id === s.id)).map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Students */}
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Students in this Advisory</Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {group.students?.map((student) => (
                        <span key={student.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-muted">
                          {student.first_name} {student.last_name}
                          <button onClick={async () => { await assignStudentToAdvisory("none", student.id); toast.success("Student removed"); fetchData(); }} className="hover:text-red-600">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                          </button>
                        </span>
                      ))}
                      {(!group.students || group.students.length === 0) && <span className="text-sm text-muted-foreground italic">No students assigned</span>}
                    </div>
                    <Select value="" onValueChange={async (studentId) => { if (studentId) { await assignStudentToAdvisory(group.group, studentId); toast.success("Student added"); fetchData(); } }}>
                      <SelectTrigger className="w-64" data-testid={`add-student-to-advisory-${group.group}`}><SelectValue placeholder="Add student to this advisory..." /></SelectTrigger>
                      <SelectContent>
                        {allStudents.filter(s => !group.students?.some(gs => gs.id === s.id)).sort((a, b) => a.last_name.localeCompare(b.last_name)).map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.last_name}, {s.preferred_name || s.first_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
